import { createContext, useEffect } from 'react';
import { languagesCodes } from '../entities';
import useRefState from '../hooks/ref-state.hook';
import { IInstanceInfo, IPreset, PresetId } from '../interfaces/instaces.interfaces';
import { Languages } from '../interfaces/misc.interface';

export interface GameCtx {
  values: {
    presetId: PresetId;
    playId: string;
    score: number;
    instance: IInstanceInfo;
    language: Languages['language'];
    theme: any;
    allInstances: IInstanceInfo[];
  };
  setPresetId: (a: PresetId) => void;
  setPlayId: (a: string) => void;
  getPlayId: () => string;
  setScore: (a: number) => void;
  setLanguage: (a: Languages['language']) => void;
  getLanguage: () => Languages['language'];
  setAllInstances: (a: IInstanceInfo[]) => void;
  getAllInstances: () => IInstanceInfo[];
  getPreset: () => IPreset;
  setInstance: (a: IInstanceInfo) => void;
  getInstance: () => IInstanceInfo;
  getTheme: () => { main: string[]; variants: string[][] };
  deleteGame: () => void;
  getLabelsSet: (instance?: IInstanceInfo, c?: PresetId) => IInstanceInfo['labelsSet'];
}

export const GameContext = createContext<GameCtx>({
  values: { presetId: -1, playId: null, score: -1, instance: null, language: null, theme: null, allInstances: [] },
  setPresetId: (a: PresetId) => {},
  setPlayId: (a: string) => {},
  getPlayId: () => '',
  setScore: (a: number) => {},
  setLanguage: (a: Languages['language']) => {},
  setInstance: (a: IInstanceInfo) => {},
  getTheme: () => ({}) as any,
  getInstance: () => ({}) as IInstanceInfo,
  setAllInstances: (a: IInstanceInfo[]) => ({}) as IInstanceInfo,
  getAllInstances: () => [] as IInstanceInfo[],
  getLanguage: () => '' as Languages['language'],
  getPreset: () => ({}) as IPreset,
  deleteGame: () => {},
  getLabelsSet: (instance?: IInstanceInfo, c?: PresetId) => ({}) as IInstanceInfo['labelsSet'],
});

export const GameCtxProvider = ({ children, instance }: any) => {
  const init = (): GameCtx['values'] => {
    // const savedGameCtxValuesString = localStorage.getItem('loq-chl:gameCtxValues');

    // if (savedGameCtxValuesString) {
    //   const savedGameCtxValues = JSON.parse(savedGameCtxValuesString) as GameCtx['values'];

    //   return savedGameCtxValues;
    // }
    return { presetId: -1, playId: null, score: -1, instance, language: null, theme: setTheme(), allInstances: [] };
  };

  const setTheme = (colors?: string[][]) => {
    if (typeof colors === 'undefined') {
      return { main: new Array(10), variants: new Array(10).fill([]) };
    }
    const main = colors.map((c) => c[0]);
    const variants = colors.map((c) => {
      const res = [...c];
      res.shift();
      return res;
    });
    return { main, variants };
  };

  const [getState, setState] = useRefState<GameCtx['values']>(init());

  // const saveGameCtxValues = () => {
  //   if (getState().presetId !== null && window.location.pathname.includes('/game')) {
  //     localStorage.setItem('loq-chl:gameCtxValues', JSON.stringify(getState()));
  //   }
  // };

  useEffect(() => {
    // window.addEventListener('unload', saveGameCtxValues);
    localStorage.removeItem('loq-chl:gameCtxValues');
    return () => {
      // window.removeEventListener('unload', saveGameCtxValues);
    };
  }, []);

  useEffect(() => {
    instance && setState((state) => ({ ...state, instance }));
  }, [instance]);

  const setPresetId = (presetId: PresetId) => {
    setState((state) => ({ ...state, presetId }));
  };

  const setPlayId = (playId: string) => {
    setState((state) => ({ ...state, playId }));
  };

  const setLanguage = (language: Languages['language']) => {
    if (languagesCodes.includes(language)) {
      setState((state) => ({ ...state, language }));
    } else {
      console.error(language);
      throw new Error('invalid language parameter');
    }
  };

  const setScore = (score: number) => {
    setState((state) => ({ ...state, score }));
  };

  const setInstance = (instance: IInstanceInfo) => {
    setState((state) => ({
      ...state,
      instance,
      theme: setTheme(instance.configurations.colors),
    }));
  };

  const getAllInstances = () => {
    return getState().allInstances;
  };

  const setAllInstances = (allInstances: IInstanceInfo[]) => {
    setState((state) => ({
      ...state,
      allInstances,
    }));
  };

  const getInstance = () => {
    return getState().instance;
  };

  const getLanguage = () => {
    return getState().language;
  };

  const getPlayId = () => {
    return getState().playId;
  };

  const getPreset = () => {
    if (!getState().instance) {
      return null;
    }
    return getState().instance.presets.find((preset) => preset.id === getState().presetId);
  };

  const getLabelsSet = (instance?: IInstanceInfo, pId?: PresetId) => {
    const rawLS = instance ? instance.labelsSet : getState().instance.labelsSet;
    if ((getState().presetId === null || getState().presetId === -1) && !pId) {
      return rawLS;
    }
    const chosenPresetLabels = (rawLS.presets || []).find((p) => p.presetId === (pId ?? getState().presetId));
    return {
      ...rawLS,
      presets: [chosenPresetLabels],
    };
  };

  const getTheme = () => {
    return getState().theme;
  };

  const deleteGame = () => {
    localStorage.removeItem('loq-chl:userRespCD');
    localStorage.removeItem('loq-chl:score');
    localStorage.removeItem('loq-chl:gameData');
    localStorage.removeItem('loq-chl:gameSave');
    localStorage.removeItem('loq-chl:gameDifficulty');
    setPlayId(null);
  };

  return (
    <GameContext.Provider
      value={{
        values: getState(),
        setPresetId,
        setPlayId,
        getPlayId,
        setScore,
        getPreset,
        setInstance,
        getInstance,
        deleteGame,
        setLanguage,
        getLanguage,
        getTheme,
        getLabelsSet,
        setAllInstances,
        getAllInstances,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
